/**
 * Wrapper for the "Possessive configuration" form section (e.g. PossessiveScope,
 * PossessiveTargetAttribute, PossessiveActorsAttribute). Used by EntityFormTab
 * when layout is 'possessive-group'. Used by EntityUpsertForm.
 */

import React from 'react';
import { Paper, Text } from '@mantine/core';
import type { FormAttribute } from './EntityUpsertForm.types';

export interface PossessiveConfigurationSectionProps {
  /** Attributes to render inside the section (order preserved). */
  attributes?: FormAttribute[] | null;
  /** Render one attribute row. */
  renderField: (attr: FormAttribute, index: number) => React.ReactNode;
  /** Optional section title. */
  title?: string;
  /** Optional description below the title. */
  description?: string;
}

const DEFAULT_TITLE = 'Possessive configuration';
const DEFAULT_DESCRIPTION =
  'Select the entity type (Possessive Scope) first; it determines the options for the attributes below.';

export function PossessiveConfigurationSection({
  attributes,
  renderField,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
}: PossessiveConfigurationSectionProps): React.ReactElement | null {
  if (!attributes || attributes.length === 0) return null;

  return (
    <Paper
      withBorder
      p="md"
      mb="md"
      bg="var(--mantine-color-default-hover)"
    >
      <Text size="sm" fw={600} c="dimmed" mb="sm">
        {title}
      </Text>
      <Text size="xs" c="dimmed" display="block" mb="md">
        {description}
      </Text>
      {attributes.map((attr, index) => (
        <React.Fragment key={getAttributeKey(attr, index)}>
          {renderField(attr, index)}
        </React.Fragment>
      ))}
    </Paper>
  );
}

function getAttributeKey(attr: FormAttribute, index: number): string {
  const name = attr.name || attr.id || attr.value;
  return (name && typeof name === 'string' ? name : '') || `attr-${index}`;
}
